import { useEffect } from "react";
import { Navigate, NavLink, Outlet, useNavigate, useOutletContext } from "react-router";

import type { LoadResource, StreamResource } from "@prequel/import-react";
import { useLoad, useStream } from "@prequel/import-react";
import { checkTabIsActive, IdValue, Spinner } from "@prequel/shared-components";

import DetailPageLayout from "@/components/import/DetailPageLayout";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import { useToast } from "@/providers/ToastProvider";

export type LoadContext = {
  load: LoadResource;
  stream: StreamResource | undefined;
};

export function useLoadContext(): LoadContext {
  return useOutletContext<LoadContext>();
}

const LoadPage = () => {
  const streamID = useRequiredParam("streamID");
  const loadID = useRequiredParam("loadID");
  const { addToast } = useToast();
  const { data: load, error, isLoading } = useLoad(loadID);
  const { data: stream } = useStream(streamID);
  const navigate = useNavigate();
  const basePath = `/import/streams/${streamID}/loads/${loadID}`;

  useEffect(() => {
    if (error) {
      addToast({ kind: "ERROR", message: "Failed to load job details." });
    }
  }, [error, addToast]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!load) {
    return <Navigate to={`/import/streams/${streamID}`} replace />;
  }

  const tabs = [
    <NavLink key="Details" to={basePath} className={checkTabIsActive} end>
      Details
    </NavLink>,
    <NavLink key="Records" to={`${basePath}/records`} className={checkTabIsActive} end>
      Records
    </NavLink>,
  ];

  return (
    <DetailPageLayout
      title="Load Job"
      // The parent crumb is the stream, so it takes the stream's name rather than this page's title.
      dynamicLabel={stream?.name ?? "Stream Details"}
      onGoBack={() => navigate(`/import/streams/${streamID}`)}
      subheading={
        <div className="flex items-center gap-2">
          <IdValue id={load.id} />
        </div>
      }
      tabs={tabs}
    >
      <Outlet context={{ load, stream }} />
    </DetailPageLayout>
  );
};

export default LoadPage;
